import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { LogOut, Copy, Plus, Users, TrendingUp, Euro, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../auth";
import { Btn, Badge, eur } from "../components/common";
import { Modal, Field, Selecter, TextArea } from "./crm/crmui";

const TABS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "leads", label: "Os meus leads" },
  { key: "comissoes", label: "Comissoes" },
];

export default function PartnerPortal() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [me, setMe] = useState(null);
  const [leads, setLeads] = useState([]);
  const [comissoes, setComissoes] = useState([]);
  const [showSubmit, setShowSubmit] = useState(false);
  const [form, setForm] = useState({ nome_contacto: "", email: "", telefone: "", organizacao: "", nif: "", plataforma: "", mensagem: "" });

  const load = async () => {
    try {
      const [m, l, c] = await Promise.all([
        api.get("/partner-portal/me"),
        api.get("/partner-portal/leads"),
        api.get("/partner-portal/commissions"),
      ]);
      setMe(m.data); setLeads(l.data || []); setComissoes(c.data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submitLead = async () => {
    if (!form.nome_contacto.trim()) return toast.error("Indique nome do contacto.");
    try {
      await api.post("/partner-portal/submit-lead", form);
      toast.success("Lead submetido!");
      setShowSubmit(false);
      setForm({ nome_contacto: "", email: "", telefone: "", organizacao: "", nif: "", plataforma: "", mensagem: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const copyLink = async () => {
    if (!me?.codigo) return;
    const url = `${window.location.origin}/f/partner-${me.codigo}`;
    try { await navigator.clipboard.writeText(url); toast.success("Link copiado."); } catch {}
  };

  const doLogout = () => { logout(); nav("/login"); };

  if (!me) return <div className="min-h-screen flex items-center justify-center text-muted2">A carregar portal...</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-navy text-white px-6 py-3 flex items-center justify-between">
        <div>
          <div className="text-xs opacity-70">Portal do Parceiro</div>
          <div className="font-bold">{me.nome}</div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={copyLink} title="Copiar link de referral" data-testid="portal-copy-link"
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-white/10 rounded text-xs hover:bg-white/20"><Copy size={12} /> {me.codigo}</button>
          <button onClick={load} className="p-1.5 hover:bg-white/10 rounded"><RefreshCw size={14} /></button>
          <button onClick={doLogout} data-testid="portal-logout" className="inline-flex items-center gap-1 px-3 py-1.5 bg-white/10 rounded text-xs hover:bg-white/20"><LogOut size={12} /> Sair</button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-6">
        <div className="flex gap-2 mb-4">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`portal-tab-${t.key}`}
              className={`text-xs px-3 py-1.5 rounded ${tab === t.key ? "bg-navy text-white" : "border border-tint text-slate-600 hover:bg-white"}`}>
              {t.label}
            </button>
          ))}
          <div className="flex-1" />
          <Btn onClick={() => setShowSubmit(true)} testid="portal-submit-btn"><Plus size={14} /> Submeter lead</Btn>
        </div>

        {tab === "dashboard" && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="portal-dashboard">
            <StatCard icon={Users} label="Leads" value={me.n_leads} color="text-navy" />
            <StatCard icon={TrendingUp} label="Clientes ganhos" value={me.n_clientes} color="text-emerald-600" />
            <StatCard icon={Euro} label="Comissao pendente" value={eur(me.pendente || 0)} color="text-amber-600" />
            <StatCard icon={Euro} label="Comissao paga" value={eur(me.paga || 0)} color="text-emerald-700" />
          </div>
        )}

        {tab === "leads" && (
          <div className="space-y-2" data-testid="portal-leads">
            {leads.length === 0 && <div className="text-center text-muted2 border border-tint rounded-lg py-12 bg-white">Ainda nao submeteu leads.</div>}
            {leads.map((l) => (
              <div key={l.id} className="bg-white border border-tint rounded-lg px-3 py-2 flex items-center gap-3" data-testid={`portal-lead-${l.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-navy truncate">{l.nome}</div>
                  <div className="text-[11px] text-muted2 flex items-center gap-2 mt-0.5">
                    {l.codigo && <span className="font-mono text-blue">{l.codigo}</span>}
                    {l.nif && <span>NIF {l.nif}</span>}
                    <Badge value={l.ciclo_vida || "lead"} />
                  </div>
                </div>
                <Badge value={l.tipo} />
                <div className="text-lg font-bold text-navy tabular-nums w-12 text-right">{l.lead_score || 0}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "comissoes" && (
          <div className="space-y-2" data-testid="portal-comissoes">
            {comissoes.length === 0 && <div className="text-center text-muted2 border border-tint rounded-lg py-12 bg-white">Ainda sem comissoes.</div>}
            {comissoes.map((c) => (
              <div key={c.id} className="bg-white border border-tint rounded-lg px-3 py-2 flex items-center gap-3" data-testid={`portal-com-${c.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-navy truncate">{c.account_nome}</div>
                  <div className="text-[11px] text-muted2 mt-0.5">{c.origem} - {c.ref_id} - base {eur(c.valor_base)}</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-navy">{eur(c.valor)}</div>
                  <div className="text-[10px] text-muted2">{c.taxa_percentual}%</div>
                </div>
                <Badge value={c.estado} />
              </div>
            ))}
          </div>
        )}
      </main>

      <Modal open={showSubmit} onClose={() => setShowSubmit(false)} title="Submeter novo lead" testid="portal-submit-modal">
        <p className="text-xs text-muted2">O lead sera automaticamente associado a si e recebera comissao quando converter em cliente.</p>
        <Field label="Nome do contacto" value={form.nome_contacto} onChange={(v) => setForm({ ...form, nome_contacto: v })} testid="portal-form-nome" />
        <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="portal-form-email" />
        <Field label="Telefone" value={form.telefone} onChange={(v) => setForm({ ...form, telefone: v })} testid="portal-form-telefone" />
        <Field label="Organizacao" value={form.organizacao} onChange={(v) => setForm({ ...form, organizacao: v })} testid="portal-form-org" />
        <Field label="NIF" value={form.nif} onChange={(v) => setForm({ ...form, nif: v })} testid="portal-form-nif" />
        <Selecter label="Plataforma de interesse" value={form.plataforma} onChange={(v) => setForm({ ...form, plataforma: v })}
          options={[{ value: "", label: "-" }, "Cognietica", "Privaxia", "Assiduo", "Recrutai"]}
          testid="portal-form-plat" />
        <TextArea label="Mensagem" value={form.mensagem} onChange={(v) => setForm({ ...form, mensagem: v })} testid="portal-form-msg" />
        <div className="flex justify-end gap-2 pt-2">
          <Btn variant="ghost" onClick={() => setShowSubmit(false)}>Cancelar</Btn>
          <Btn onClick={submitLead} testid="portal-submit-confirm">Submeter</Btn>
        </div>
      </Modal>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-tint rounded-lg p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} className="text-slate-400" />
        <div className="text-[10px] uppercase tracking-wider text-muted2">{label}</div>
      </div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
    </div>
  );
}
